import { ConfigurationDropdown } from "@/types";
import { IconMovie, IconPhoto, IconPhotoVideo, IconRotate2 } from "@tabler/icons-react";
import LabelActions from "@/screens/creative-label/components/LabelActions";
import { Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { AccountBrand } from "@/screens/analysis/types";
import { AddCompetitorDialog } from "@/screens/analysis/components/competitor/brands/AddCompetitorDialog";
import { LuPlus } from "react-icons/lu";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  RiArrowDownSLine,
  RiColorFilterLine,
  RiInformationLine,
  RiStackLine,
} from "@remixicon/react";

export interface CreativeReportDropdownsProps {
  accountId?: string;
  sectionId?: string;
  dropdowns: ConfigurationDropdown[];
  dropdownsValues: {
    [key: string]: string | string[] | null;
  };
  onChange: (dropdownsValues: { [key: string]: string | string[] | null }) => void;
  disabled?: boolean;
  handleLabelDelete?: (labelId: string) => Promise<void>;
  onBrandAdded?: (brand: AccountBrand) => void;
  viewMode?: "table" | "grid" | "graph";
}

const CreativeReportDropdowns = ({
  accountId,
  sectionId,
  dropdowns,
  dropdownsValues,
  onChange,
  handleLabelDelete,
  onBrandAdded,
  disabled,
  viewMode,
}: CreativeReportDropdownsProps) => {
  //   handles competitors auto sync
  const [isAutoSync, setIsAutoSync] = useState<boolean>(false);
  const [showInitialContent, setShowInitialContent] = useState<{
    [key: string]: boolean;
  }>({});
  const [isAddCompetitorDialogOpen, setIsAddCompetitorDialogOpen] = useState(false);

  // Function to check if a dropdown should be visible based on its dependencies
  const shouldShowDropdown = useCallback(
    (dropdown: ConfigurationDropdown): boolean => {
      if (!dropdown.depends_on || dropdown.depends_on.length === 0) {
        return true; // No dependencies, always show
      }

      const logic = dropdown.logic || "all";

      const dependencyChecks = dropdown.depends_on.map((dependency) => {
        const selectedValue = dropdownsValues?.[dependency.key];

        if (!selectedValue || typeof selectedValue !== "string") {
          return false; // Dependency not selected or invalid type
        }

        // Find the selected option in the dependency dropdown
        const dependencyDropdown = dropdowns.find((d) => d.key === dependency.key);
        const selectedOption = dependencyDropdown?.options.find(
          (opt) => opt.key === selectedValue,
        );

        if (!selectedOption) {
          return false; // Selected option not found
        }

        // Check if the field value matches the expected value
        type OptionWithField = typeof selectedOption & Record<string, unknown>;
        const fieldValue = (selectedOption as OptionWithField)[dependency.field];
        return fieldValue === dependency.value;
      });

      // Apply logic (all or any)
      return logic === "all"
        ? dependencyChecks.every(Boolean)
        : dependencyChecks.some(Boolean);
    },
    [dropdowns, dropdownsValues],
  );

  // Effect to set default value for label_view if selected option doesn't exist
  useEffect(() => {
    const labelViewDropdown = dropdowns?.find((d) => d.key === "label_view");
    if (labelViewDropdown && labelViewDropdown.options.length > 0) {
      // Only apply defaults when the dropdown is visible to avoid loops
      const isLabelViewVisible = shouldShowDropdown(labelViewDropdown);

      if (!isLabelViewVisible) return;

      const selectedValue = dropdownsValues?.["label_view"];
      const creativeType = dropdownsValues?.["creative_type"];
      const selectedOptionExists = labelViewDropdown.options.some(
        (option) => option.key === selectedValue,
      );

      // If creative_type is not video, always set label_view to "multiple"
      if (creativeType !== "video") {
        const multipleOption = labelViewDropdown.options.find(
          (option) => option.key === "multiple",
        );

        if (multipleOption && selectedValue !== "multiple") {
          onChange({
            ...dropdownsValues,
            label_view: "multiple",
          });
        }
      } else if (!selectedOptionExists) {
        // For video creative_type, check if selected option exists, otherwise fallback
        const multipleOption = labelViewDropdown.options.find(
          (option) => option.key === "multiple",
        );
        const defaultOption = multipleOption || labelViewDropdown.options[0];

        onChange({
          ...dropdownsValues,
          label_view: defaultOption.key,
        });
      }
    }
  }, [dropdowns, dropdownsValues, onChange, shouldShowDropdown]);

  const handleAutoSync = (autoSync: boolean) => {
    setIsAutoSync(autoSync);
    if (autoSync) {
      onChange({ ...dropdownsValues, competitors: ["all"] });
    } else {
      // hide initial page on turn off
      setShowInitialContent((prev) => ({ ...prev, competitors: false }));
    }
  };

  // handles group by
  const [searchParams, setSearchParams] = useSearchParams();

  const handleCreateLabel = () => {
    const params = new URLSearchParams(searchParams);
    params.set("type", "label");
    params.set("apply", "true");
    if (sectionId) {
      params.set("sectionId", sectionId);
    } else {
      params.delete("sectionId");
    }
    setSearchParams(params);
  };

  const selectedGroupByOption = dropdowns
    ?.find((d) => d.key === "group_by")
    ?.options?.find((op) => op.key === dropdownsValues["group_by"]);

  const disableMediaTypeDropdown =
    disabled ||
    selectedGroupByOption?.creative_type === "image" ||
    selectedGroupByOption?.creative_type === "video";

  // Effect to clear values of dropdowns that are currently hidden
  useEffect(() => {
    if (!dropdowns || !dropdownsValues) return;

    const visibleKeys = new Set(dropdowns.filter(shouldShowDropdown).map((d) => d.key));

    const nextValues: { [key: string]: string | string[] | null } = {
      ...dropdownsValues,
    };
    let didChange = false;

    dropdowns.forEach((d) => {
      const key = d.key;
      if (!visibleKeys.has(key) && nextValues[key] != null) {
        nextValues[key] = null;
        didChange = true;
      }
    });

    if (didChange) {
      onChange(nextValues);
    }
  }, [dropdowns, dropdownsValues, onChange, shouldShowDropdown]);

  return (
    <>
      {dropdowns?.filter(shouldShowDropdown).map((dropdown) => {
        const selectedValue = dropdownsValues?.[dropdown.key] ?? null;
        let selectedOption =
          dropdown.type === "multi_select"
            ? dropdown.options
                .filter((op) => selectedValue?.includes(op.key))
                .map((op) => ({
                  key: op.key,
                  label: op.label,
                  ...(dropdown.key === "competitors" && op?.profile_image_url
                    ? {
                        icon: (
                          <img
                            className="h-5 w-5 rounded-[6px]"
                            alt="competitor profile"
                            src={op?.profile_image_url}
                          />
                        ),
                      }
                    : {}),
                }))
            : dropdown.options.find((op) => op.key === selectedValue);

        const options: ConfigurationDropdownItem[] = dropdown.options.map((op) => ({
          key: op.key,
          label: op.label,
          info: op.info,
          ...(dropdown.key === "group_by" && op.column_type === "text_ai_label"
            ? {
                creative_type: op.creative_type,
                icon:
                  op.creative_type === "image" ? (
                    <IconPhoto size={16} color="#FF4699" className="shrink-0" />
                  ) : op.creative_type === "video" ? (
                    <IconMovie size={17} color="#018C62" className="shrink-0" />
                  ) : (
                    <IconPhotoVideo size={17} color="#5344FF" className="shrink-0" />
                  ),
                action: (
                  <LabelActions
                    type="label"
                    itemKey={op.key}
                    itemLabel={op.label}
                    accountId={accountId}
                    handleItemDelete={async () => handleLabelDelete?.(op.key)}
                    sectionId={sectionId}
                  />
                ),
              }
            : dropdown.key === "competitors" && op?.profile_image_url
              ? {
                  icon: (
                    <img
                      className="h-5 w-5 rounded-[6px]"
                      src={op?.profile_image_url}
                    ></img>
                  ),
                }
              : dropdown.key === "creative_type"
                ? {
                    icon:
                      op.key === "image" ? (
                        <IconPhoto size={16} color="#FF4699" className="shrink-0" />
                      ) : op.key === "video" ? (
                        <IconMovie size={17} color="#018C62" className="shrink-0" />
                      ) : (
                        <IconPhotoVideo size={17} color="#5344FF" className="shrink-0" />
                      ),
                  }
                : dropdown.key === "label_view"
                  ? {
                      icon:
                        op.key === "sequence" ? (
                          <RiStackLine
                            size={14}
                            className="shrink-0 text-brand-primary-400"
                          />
                        ) : op.key === "multiple" ? (
                          <RiColorFilterLine
                            size={14}
                            className="shrink-0 text-brand-primary-400"
                          />
                        ) : null,
                      disabled:
                        op.key === "sequence" &&
                        (dropdownsValues?.["creative_type"] !== "video" ||
                          viewMode === "graph"),
                      disabledInfo:
                        op.key === "sequence"
                          ? viewMode === "graph"
                            ? "Graph isn't supported in Sequence."
                            : dropdownsValues?.["creative_type"] !== "video"
                              ? "Sequences are Video-only."
                              : undefined
                          : undefined,
                    }
                  : {}),
        }));

        let hideSearchbar = true;
        let searchPlaceholder = "Search";
        let _disabled = disabled;
        let contentClassName = "w-[200px]";
        let triggerMaxWidth = "200px";

        let cta = null;
        let header = null;
        let footerCTA = null;
        let maxHeight = 324;
        let initialPageContent = null;

        let disableAllOptions = false;
        let selectAllOptions = false;

        if (dropdown.key === "group_by") {
          cta = (
            <div
              className="flex items-center px-2 py-0 h-8 gap-2 cursor-pointer hover:bg-[#F6F6F6] rounded-sm mb-1"
              onClick={handleCreateLabel}
            >
              <Plus size={16} color="#FF4500" />
              <span className="text-[13px] text-[#FF4500]">Create Label</span>
            </div>
          );
          hideSearchbar = false;

          // Sort options: first no creative_type key, then null/undefined, then video, then image
          options.sort((a, b) => {
            // First, sort by creative_type priority
            const getCreativeTypePriority = (option: ConfigurationDropdownItem) => {
              if (!("creative_type" in option)) return 1; // no creative_type key gets priority 1
              if (!option.creative_type || option.creative_type === "all") return 2; // null/undefined creative_type gets priority 2
              if (option.creative_type === "video") return 3;
              if (option.creative_type === "image") return 4;
              return 5; // any other types
            };

            const aPriority = getCreativeTypePriority(a);
            const bPriority = getCreativeTypePriority(b);

            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }

            // If creative_type priority is the same, sort alphabetically by label
            return a.label.localeCompare(b.label);
          });
        }

        if (dropdown.key === "creative_type") {
          if (disableMediaTypeDropdown) _disabled = true;
          contentClassName = "w-[154px]";
        }
        if (dropdown.key === "competitors") {
          // select all value based on current applied value
          if (dropdownsValues?.["competitors"]?.includes("all")) {
            selectedOption = [{ key: "all", label: "Auto-Sync" }];
          }
          // disable/enables based on current status of auto sync
          if (isAutoSync) {
            disableAllOptions = true;
            selectAllOptions = true;
          }
          triggerMaxWidth = "400px";
          searchPlaceholder = "Search brand name";
          contentClassName = "w-[324px]";
          maxHeight = 234;
          header = (
            <div className="border-b flex items-center justify-between py-1 px-2">
              <span className="text-[12px] font-[500] leading-[20px] tracking-[0%] text-brand-primary-800">
                Competitors
              </span>
              <div className="flex gap-2 items-center">
                <span className="flex items-center gap-1">
                  <IconRotate2 size={14} className="shrink-0" color="#021859CC" />
                  <p className="text-[12px] font-normal leading-[20px] tracking-[0%] text-right text-brand-primary-800 relative top-[1px]">
                    Auto sync brands
                  </p>
                </span>
                <Switch
                  checked={isAutoSync}
                  onCheckedChange={handleAutoSync}
                  className="data-[state=checked]:bg-[#FF4405] data-[state=checked]:border-[#FF4405] h-4 w-7"
                  thumbClassName="h-3 w-3 data-[state=checked]:translate-x-3"
                />
              </div>
            </div>
          );
          footerCTA = (
            <>
              <Button
                variant="outline"
                onClick={() => setIsAddCompetitorDialogOpen(true)}
              >
                <LuPlus className="shrink-0" size={16} />
                <span className="mt-[1px]">Add Brand</span>
              </Button>
              <AddCompetitorDialog
                accountId={accountId}
                isOpen={isAddCompetitorDialogOpen}
                onOpenChange={setIsAddCompetitorDialogOpen}
                onBrandAdded={onBrandAdded ? onBrandAdded : () => {}}
              />
            </>
          );
          hideSearchbar = false;
          if (showInitialContent[dropdown.key] === true) {
            initialPageContent = (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <IconRotate2 size={24} color="#021859CC" />
                <div className="text-[12px] text-brand-primary-800 w-[184px] text-center">
                  <span className="font-semibold">Auto-sync</span> adds all new brands
                  automatically, switch off for manual selection.
                </div>
                <Button
                  variant="outline"
                  className="mt-2"
                  onClick={() =>
                    setShowInitialContent((prev) => ({ ...prev, [dropdown.key]: false }))
                  }
                >
                  Show Brands
                </Button>
              </div>
            );
          }
        }

        return (
          <CreativeDropdown
            disableAllOptions={disableAllOptions}
            selectAllOptions={selectAllOptions}
            initialPageContent={initialPageContent}
            triggerMaxWidth={triggerMaxWidth}
            maxScrollHeight={maxHeight}
            footerCTA={footerCTA}
            header={header}
            contentClassName={contentClassName}
            disabled={_disabled}
            hideSearchbar={hideSearchbar}
            searchPlaceholder={searchPlaceholder}
            cta={cta}
            label={dropdown.label}
            type={dropdown.type}
            key={dropdown.key}
            options={options}
            selectedOption={selectedOption}
            onOpenChange={(open) => {
              if (dropdown.key === "competitors") {
                // onOpen competitors dropdown sets the correct auto sync and display initial Content
                if (open) {
                  if (dropdownsValues?.["competitors"]?.includes("all")) {
                    setIsAutoSync(true);
                    setShowInitialContent((prev) => ({ ...prev, competitors: true }));
                  }
                }
              }
            }}
            onChange={(item: ConfigurationDropdownItem | ConfigurationDropdownItem[]) => {
              const isArray = Array.isArray(item);
              const value = isArray ? item.map((i) => i.key) : item.key;
              const updatedDropdownValues = {
                ...(dropdownsValues ?? {}),
                [dropdown.key]: value,
              };
              if (!isArray && dropdown.key === "group_by") {
                updatedDropdownValues["creative_type"] = item?.creative_type ?? null;
              }
              onChange(updatedDropdownValues);
            }}
          />
        );
      })}
    </>
  );
};

export default CreativeReportDropdowns;

export type DropdownType = "single_select" | "multi_select";
interface CreativeDropdownProps {
  label: string;
  type: DropdownType;
  options: ConfigurationDropdownItem[];
  selectedOption?: ConfigurationDropdownItem | ConfigurationDropdownItem[];
  onChange?: (option: ConfigurationDropdownItem | ConfigurationDropdownItem[]) => void;
  disabled?: boolean;
  hideSearchbar?: boolean;
  searchPlaceholder?: string;
  contentClassName?: string;
  maxScrollHeight?: number;
  triggerMaxWidth?: string;
  onOpenChange?: (open: boolean) => void;
  cta?: React.ReactNode | React.ReactElement;
  header?: React.ReactNode;
  footerCTA?: React.ReactNode;
  initialPageContent?: React.ReactNode;
  disableAllOptions?: boolean;
  selectAllOptions?: boolean;
}

export interface ConfigurationDropdownItem {
  key: string;
  label: string;
  icon?: React.ReactNode | React.ReactElement;
  info?: string;
  action?: React.ReactNode;
  creative_type?: string | null;
  disabled?: boolean;
  disabledInfo?: string;
}

const CreativeDropdown = ({
  disabled,
  label,
  type,
  options,
  hideSearchbar,
  selectedOption,
  onChange,
  cta,
  searchPlaceholder,
  contentClassName,
  header,
  footerCTA,
  maxScrollHeight = 324,
  triggerMaxWidth = "200px",
  initialPageContent,
  onOpenChange,
  disableAllOptions,
  selectAllOptions,
}: CreativeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Update tempSelectedItems when selectAllOptions changes
  useEffect(() => {
    if (selectAllOptions && type === "multi_select") {
      setTempSelectedItems(options);
    }
  }, [selectAllOptions, options, type]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getDisplayLabel = () => {
    if (Array.isArray(selectedOption)) {
      if (selectedOption?.[0]?.icon) {
        const displayItems = selectedOption.slice(0, 5);
        const remainingCount = selectedOption.length - 5;
        return (
          <div className="flex gap-2 items-center">
            <div className="flex gap-2 items-center">
              {displayItems.map((option) => (
                <React.Fragment key={option.key}>{option.icon}</React.Fragment>
              ))}
              {remainingCount > 0 && (
                <span className="text-xs text-brand-primary-800 relative top-[1px]">
                  +{remainingCount}
                </span>
              )}
            </div>
          </div>
        );
      }

      return selectedOption.map((option) => option.label).join(", ");
    }
    return selectedOption?.label ?? "";
  };

  //   handle Single Select
  const handleSingleSelect = (item: ConfigurationDropdownItem) => {
    if (disableAllOptions) return;
    onChange?.(item);
    setIsOpen(false);
  };

  //   handle Multi Select
  const [tempSelectedItems, setTempSelectedItems] = useState<ConfigurationDropdownItem[]>(
    Array.isArray(selectedOption) ? selectedOption : [],
  );

  const handleMultiSelect = (item: ConfigurationDropdownItem) => {
    if (disableAllOptions) return;
    const isSelected = tempSelectedItems.some((selected) => selected.key === item.key);
    if (isSelected) {
      setTempSelectedItems(tempSelectedItems.filter((i) => i.key !== item.key));
    } else {
      setTempSelectedItems([...tempSelectedItems, item]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (disableAllOptions) return;
    if (checked) {
      setTempSelectedItems(filteredOptions);
    } else {
      setTempSelectedItems([]);
    }
  };

  const confirmSelection = () => {
    setIsOpen(false);
    if (!disableAllOptions) {
      onChange?.(tempSelectedItems);
    }
  };

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        onOpenChange?.(open);
        if (!open && type === "multi_select") {
          // Reset to original selection when closing without confirming
          setTempSelectedItems(Array.isArray(selectedOption) ? selectedOption : []);
        }
      }}
      modal={true}
    >
      <DropdownMenuTrigger
        disabled={disabled}
        style={{ maxWidth: triggerMaxWidth }}
        className="relative flex h-8 items-center justify-between whitespace-nowrap  squircle squircle-radius-[8px]  bg-transparent px-3 py-2 text-sm drop-shadow-sm focus:outline-none focus:ring-0 squircle-fill-[white] hover:squircle-fill-hover-gray
        before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none"
      >
        <div className="mr-2 w-full flex items-center gap-2 overflow-hidden">
          <span className="shrink-0 overflow-hidden text-[#67749B] text-ellipsis text-xs font-normal leading-none tracking-[0.24px]">
            {label}
          </span>
          <span className="flex-1 truncate text-brand-primary-new text-xs font-medium leading-none tracking-[0.24px]">
            {getDisplayLabel()}
          </span>
        </div>
        {!disabled && <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn("rounded-[12px]", contentClassName)}
      >
        {header && <>{header}</>}
        {initialPageContent ? (
          <>{initialPageContent}</>
        ) : (
          <div>
            {cta && (
              <>
                {cta}
                <Separator className="" />
              </>
            )}
            {!hideSearchbar && (
              <>
                <div className="flex items-center gap-2 px-[8px] py-[0px]">
                  {type === "multi_select" && (
                    <>
                      <Checkbox
                        checked={
                          selectAllOptions || tempSelectedItems.length === options.length
                        }
                        onCheckedChange={handleSelectAll}
                        disabled={disableAllOptions}
                        className={cn(
                          "data-[state=checked]:bg-[#FF4405] data-[state=checked]:border-[#FF4405] border-border-gray",
                          disableAllOptions && "opacity-50 cursor-not-allowed",
                        )}
                      />
                      <span className="text-[12px] font-normal leading-[20px] text-brand-primary-800 relative top-[1px]">
                        Select All
                      </span>
                    </>
                  )}
                  <div className="flex items-center flex-1">
                    <Search size={14} color="hsl(0 0% 45.1%)" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder={searchPlaceholder ?? "Search"}
                      className="shadow-none border-0 outline-none ring-0 text-[12px] px-1 relative top-[1px]"
                    />
                  </div>
                </div>
                <Separator className="mb-1" />
              </>
            )}
            <DropdownMenuGroup
              className="overflow-y-scroll scrollbar-hide"
              style={{ maxHeight: `${maxScrollHeight}px` }}
            >
              {filteredOptions.map((item) => (
                <ConfigurationDropdownItem
                  key={item.key}
                  item={item}
                  selectedItem={
                    type === "multi_select" ? tempSelectedItems : selectedOption
                  }
                  onSelect={
                    type === "multi_select" ? handleMultiSelect : handleSingleSelect
                  }
                  isMultiSelect={type === "multi_select"}
                  disabled={disableAllOptions}
                  forceChecked={selectAllOptions}
                />
              ))}
            </DropdownMenuGroup>
          </div>
        )}
        {type === "multi_select" && (
          <div className="p-2 border-t flex justify-between">
            {footerCTA}
            <Button
              onClick={confirmSelection}
              className={cn(
                "bg-[#FF4405] text-white hover:bg-[#FF4405]/90 border-0 px-6",
                disableAllOptions && "opacity-50 cursor-not-allowed",
              )}
              disabled={disableAllOptions}
            >
              Done
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ConfigurationDropdownItemProps {
  item: ConfigurationDropdownItem;
  onSelect: (item: ConfigurationDropdownItem) => void;
  selectedItem?: ConfigurationDropdownItem | ConfigurationDropdownItem[];
  isMultiSelect: boolean;
  disabled?: boolean;
  forceChecked?: boolean;
}

const ConfigurationDropdownItem = ({
  item,
  onSelect,
  selectedItem,
  isMultiSelect,
  disabled = false,
  forceChecked = false,
}: ConfigurationDropdownItemProps) => {
  const isSelected =
    forceChecked ||
    (Array.isArray(selectedItem)
      ? selectedItem.some((selected) => selected.key === item.key)
      : selectedItem?.key === item.key);

  const isDisabled = disabled || item.disabled;

  const dropdownItem = (
    <DropdownMenuItem
      key={item.key}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isMultiSelect && isSelected) return;
        if (!isDisabled) {
          onSelect(item);
        }
      }}
      className={cn(
        "group cursor-pointer w-full rounded-[8px] hover:bg-[#F6F6F699] focus:hover:bg-[#F6F6F699] focus:bg-transparent",
        isDisabled && "cursor-not-allowed opacity-50",
        !isMultiSelect && isSelected && "bg-[#F6F6F6]",
      )}
      disabled={isDisabled}
    >
      <div className="w-full flex items-center gap-2">
        {isMultiSelect && (
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            className="data-[state=checked]:bg-[#FF4405] data-[state=checked]:border-[#FF4405] border-border-gray"
          />
        )}
        {item.icon && <div className="shrink-0">{item.icon}</div>}
        <div className="flex-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-brand-primary-new text-[13px] font-normal leading-[20px]">
              {item.label}
            </span>
          </div>
          {item.info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <RiInformationLine className="h-[14px] w-[14px] text-brand-primary-400 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-[238px]">
                  <p>{item.info}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      {item?.action && <>{item?.action}</>}
    </DropdownMenuItem>
  );

  // Wrap in tooltip if disabled and has disabledInfo
  if (isDisabled && item.disabledInfo) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full">{dropdownItem}</div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            alignOffset={100}
            className="text-[12px]"
          >
            {item.disabledInfo}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return dropdownItem;
};
